/**
 * Free Text Overlay Component
 *
 * UX Requirements:
 * - Cursor: '+' when tool active
 * - Click: Creates text box with cursor at top-left
 * - Initial: Placeholder text fully selected, auto-edit mode
 * - Single-click: Select (shows resize handles + trash)
 * - Double-click: Edit mode
 * - Auto-remove: If empty after blur
 * - No move/resize during edit
 */

import React, { useCallback, useState, useRef, useEffect } from 'react';
import { FieldContextMenu } from './FieldContextMenu';
import { EditFieldModal, type FieldData } from './EditFieldModal';
import styles from './FreeTextOverlay.module.scss';

export interface FreeText {
  id: string;
  // TWO-PHASE PAGE TRACKING
  pageId: string;    // Stable page UUID
  pageNum: number;   // Derived from pageId
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  fontSize?: number;
  fontColor?: string;
  borderColor?: string;
  backgroundColor?: string;
  horizontalAlign?: 'left' | 'center' | 'right';
}

export interface FreeTextOverlayProps {
  freeTexts: FreeText[];
  pageNum: number;
  pageWidth: number;
  pageHeight: number;
  zoom: number;
  addFreeTextMode: boolean;
  formBuilderActive?: boolean; // When true, disable free text editing (Forms mode)
  onAddFreeTextAt: (pageNum: number, x: number, y: number) => void;
  onUpdateFreeText: (id: string, text: string) => void;
  onUpdateFreeTextProperties?: (id: string, updates: Partial<FreeText>) => void;
  onMoveFreeText?: (id: string, x: number, y: number) => void;
  onDeleteFreeText?: (id: string) => void;
  onResizeFreeText?: (id: string, width: number, height: number) => void;
  onDeactivate?: () => void;
  canDeleteFreeText?: boolean; // Whether user can delete free text (Full Editing only)
  canEditFreeText?: boolean; // Whether user can edit, move, resize free text (Full Editing only)
}

export const FreeTextOverlay: React.FC<FreeTextOverlayProps> = ({
  freeTexts,
  pageNum,
  pageWidth,
  pageHeight,
  zoom,
  addFreeTextMode,
  formBuilderActive = false,
  onAddFreeTextAt,
  onUpdateFreeText,
  onUpdateFreeTextProperties,
  onMoveFreeText,
  onDeleteFreeText,
  onResizeFreeText,
  onDeactivate,
  canDeleteFreeText = true, // Default to true for backward compatibility
  canEditFreeText = true, // Default to true for backward compatibility
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [editModalId, setEditModalId] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [resizing, setResizing] = useState<{ id: string; corner: string; startX: number; startY: number; startWidth: number; startHeight: number; startBoxX: number; startBoxY: number } | null>(null);
  const [dragging, setDragging] = useState<{ id: string; startX: number; startY: number; startBoxX: number; startBoxY: number } | null>(null);

  // Filter free texts for this page
  // In readonly mode (when canEditFreeText is false), hide empty text boxes
  const pageFreeTexts = freeTexts.filter(ft => {
    if (ft.pageNum !== pageNum) return false;
    // Hide empty text boxes in readonly mode
    if (!canEditFreeText && (!ft.text || ft.text === 'Click to edit text')) return false;
    return true;
  });

  /**
   * Auto-select placeholder text when entering edit mode
   */
  useEffect(() => {
    if (editingId && textareaRef.current) {
      const textarea = textareaRef.current;
      const freeText = pageFreeTexts.find(ft => ft.id === editingId);

      // Select all text if it's the default placeholder
      if (!freeText?.text || freeText.text === 'Click to edit text') {
        setTimeout(() => {
          textarea.select();
        }, 0);
      }
    }
  }, [editingId, pageFreeTexts]);

  /**
   * Handle click on overlay to add new free text
   */
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (!addFreeTextMode || !overlayRef.current) {
      return;
    }
    if (e.target !== overlayRef.current) {
      return;
    }

    const rect = overlayRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    onAddFreeTextAt(pageNum, x, y);

    if (onDeactivate) {
      onDeactivate();
    }
  }, [addFreeTextMode, pageNum, onAddFreeTextAt, onDeactivate]);

  /**
   * Auto-edit newly created text boxes
   */
  useEffect(() => {
    const newBox = pageFreeTexts.find(ft => ft.text === 'Click to edit text');
    if (newBox && !editingId) {
      setEditingId(newBox.id);
      setSelectedId(null);
    }
  }, [pageFreeTexts, editingId]);

  const handleTextChange = useCallback((id: string, text: string) => {
    onUpdateFreeText(id, text);
  }, [onUpdateFreeText]);

  const handleBlur = useCallback((id: string, text: string) => {
    setEditingId(null);

    // Auto-remove if empty
    if (!text || text === 'Click to edit text') {
      if (onDeleteFreeText) {
        onDeleteFreeText(id);
      }
    }
  }, [onDeleteFreeText]);

  const handleSingleClick = useCallback((e: React.MouseEvent, id: string) => {
    if (formBuilderActive) return; // Disable in Forms mode
    e.stopPropagation();
    if (editingId !== id) {
      setSelectedId(id);
      setEditingId(null);

      // Only show context menu if user can delete free text
      if (canDeleteFreeText) {
        // Position context menu below the text box
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        setContextMenuId(id);
        setContextMenuPos({ x: rect.left, y: rect.bottom + 4 }); // 4px gap below box
      }
    }
  }, [editingId, formBuilderActive, canDeleteFreeText]);

  const handleDoubleClick = useCallback((e: React.MouseEvent, id: string) => {
    if (formBuilderActive) return; // Disable in Forms mode
    if (!canEditFreeText) return; // Don't allow editing if user lacks permission
    e.stopPropagation();
    setEditingId(id);
    setSelectedId(null);
  }, [formBuilderActive, canEditFreeText]);

  const handleDelete = useCallback(() => {
    if (contextMenuId && onDeleteFreeText) {
      onDeleteFreeText(contextMenuId);
    }
    setSelectedId(null);
    setContextMenuId(null);
    setContextMenuPos(null);
  }, [contextMenuId, onDeleteFreeText]);

  const handleEdit = useCallback(() => {
    if (contextMenuId) {
      setEditModalId(contextMenuId);
    }
    setContextMenuId(null);
    setContextMenuPos(null);
  }, [contextMenuId]);

  /**
   * Drag to reposition free text box
   */
  const handleDragMouseDown = useCallback((e: React.MouseEvent, id: string) => {
    if (formBuilderActive) return; // Disable in Forms mode
    if (!canEditFreeText) return; // Don't allow moving if user lacks permission

    // Don't start drag if clicking on resize handle
    if ((e.target as HTMLElement).classList.contains(styles.resizeHandle)) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    const freeText = pageFreeTexts.find(ft => ft.id === id);
    if (!freeText) return;

    setDragging({
      id,
      startX: e.clientX,
      startY: e.clientY,
      startBoxX: freeText.x,
      startBoxY: freeText.y,
    });
  }, [pageFreeTexts, formBuilderActive, canEditFreeText]);

  /**
   * Resize handle mouse down
   */
  const handleResizeMouseDown = useCallback((e: React.MouseEvent, id: string, corner: string) => {
    if (formBuilderActive) return; // Disable in Forms mode
    if (!canEditFreeText) return; // Don't allow resizing if user lacks permission

    e.stopPropagation();
    e.preventDefault();

    const freeText = pageFreeTexts.find(ft => ft.id === id);
    if (!freeText) return;

    setResizing({
      id,
      corner,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: freeText.width,
      startHeight: freeText.height,
      startBoxX: freeText.x,
      startBoxY: freeText.y,
    });
  }, [pageFreeTexts, formBuilderActive, canEditFreeText]);

  /**
   * Drag mouse move
   */
  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = (e.clientX - dragging.startX) / zoom;
      const deltaY = (e.clientY - dragging.startY) / zoom;

      const newX = dragging.startBoxX + deltaX;
      const newY = dragging.startBoxY + deltaY;

      if (onMoveFreeText) {
        onMoveFreeText(dragging.id, newX, newY);
      }
    };

    const handleMouseUp = () => {
      setDragging(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, onMoveFreeText]);

  /**
   * Resize handle mouse move
   * Anchoring: opposite corner stays fixed
   * - nw (top-left) → anchor bottom-right (se)
   * - ne (top-right) → anchor bottom-left (sw)
   * - sw (bottom-left) → anchor top-right (ne)
   * - se (bottom-right) → anchor top-left (nw)
   */
  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = (e.clientX - resizing.startX) / zoom;
      const deltaY = (e.clientY - resizing.startY) / zoom;

      let newWidth = resizing.startWidth;
      let newHeight = resizing.startHeight;
      let newX = resizing.startBoxX;
      let newY = resizing.startBoxY;

      // Resize based on corner - opposite corner is anchored
      if (resizing.corner.includes('e')) {
        // East side: expand right (anchor left)
        newWidth = resizing.startWidth + deltaX;
      } else if (resizing.corner.includes('w')) {
        // West side: expand left (anchor right)
        newWidth = resizing.startWidth - deltaX;
        newX = resizing.startBoxX + deltaX;
      }

      if (resizing.corner.includes('s')) {
        // South side: expand down (anchor top)
        newHeight = resizing.startHeight + deltaY;
      } else if (resizing.corner.includes('n')) {
        // North side: expand up (anchor bottom)
        newHeight = resizing.startHeight - deltaY;
        newY = resizing.startBoxY + deltaY;
      }

      // Enforce minimum size (but allow position to continue moving)
      const minWidth = 15;
      const minHeight = 15;

      if (newWidth < minWidth) {
        if (resizing.corner.includes('w')) {
          newX = resizing.startBoxX + (resizing.startWidth - minWidth);
        }
        newWidth = minWidth;
      }

      if (newHeight < minHeight) {
        if (resizing.corner.includes('n')) {
          newY = resizing.startBoxY + (resizing.startHeight - minHeight);
        }
        newHeight = minHeight;
      }

      // Update both size and position
      if (onResizeFreeText) {
        onResizeFreeText(resizing.id, newWidth, newHeight);
      }
      if (onMoveFreeText && (newX !== resizing.startBoxX || newY !== resizing.startBoxY)) {
        onMoveFreeText(resizing.id, newX, newY);
      }
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, onResizeFreeText, onMoveFreeText]);

  /**
   * Click outside to deselect
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (selectedId && overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
        setSelectedId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedId]);

  // Handle save from edit modal
  const handleSaveEditModal = useCallback((updates: Partial<FieldData>) => {
    if (editModalId && onUpdateFreeTextProperties) {
      onUpdateFreeTextProperties(editModalId, {
        fontSize: updates.fontSize,
        fontColor: updates.fontColor,
        borderColor: updates.borderColor,
        backgroundColor: updates.backgroundColor,
        horizontalAlign: updates.horizontalAlign,
      });
    }
    setEditModalId(null);
  }, [editModalId, onUpdateFreeTextProperties]);

  return (
    <>
      <div
        ref={overlayRef}
        className={`${styles.overlay} ${addFreeTextMode ? styles.addMode : ''}`}
        style={{ width: pageWidth, height: pageHeight }}
        onClick={handleOverlayClick}
      >
      {pageFreeTexts.map(freeText => {
        const isEditing = editingId === freeText.id;
        const isSelected = selectedId === freeText.id;

        const isDragging = dragging?.id === freeText.id;

        return (
          <div
            key={freeText.id}
            className={`${styles.freeTextWrapper} ${isSelected ? styles.selected : ''} ${isEditing ? styles.editing : ''} ${isDragging ? styles.dragging : ''}`}
            style={{
              left: `${freeText.x * zoom}px`,
              top: `${freeText.y * zoom}px`,
              width: `${freeText.width * zoom}px`,
              minHeight: `${freeText.height * zoom}px`,
              border: freeText.borderColor && freeText.borderColor !== 'transparent' ? `1px solid ${freeText.borderColor}` : 'none',
              backgroundColor: freeText.backgroundColor || '#ffffff',
              textAlign: freeText.horizontalAlign || 'left',
            }}
            onClick={(e) => handleSingleClick(e, freeText.id)}
            onDoubleClick={(e) => handleDoubleClick(e, freeText.id)}
            onMouseDown={(e) => !isEditing && handleDragMouseDown(e, freeText.id)}
          >
            {isEditing ? (
              <textarea
                ref={textareaRef}
                className={styles.textarea}
                value={freeText.text}
                onChange={(e) => handleTextChange(freeText.id, e.target.value)}
                onBlur={() => handleBlur(freeText.id, freeText.text)}
                autoFocus
                style={{
                  fontSize: `${(freeText.fontSize || 14) * zoom}px`,
                  color: freeText.fontColor || '#000000',
                  textAlign: freeText.horizontalAlign || 'left',
                }}
              />
            ) : (
              <div
                className={styles.textDisplay}
                style={{
                  fontSize: `${(freeText.fontSize || 14) * zoom}px`,
                  color: freeText.fontColor || '#000000',
                  textAlign: freeText.horizontalAlign || 'left',
                }}
              >
                {freeText.text || 'Click to edit text'}
              </div>
            )}

            {/* Show resize handles when selected (not editing) and user has edit permission */}
            {isSelected && !isEditing && canEditFreeText && (
              <>
                <div
                  className={`${styles.resizeHandle} ${styles.nw}`}
                  onMouseDown={(e) => handleResizeMouseDown(e, freeText.id, 'nw')}
                />
                <div
                  className={`${styles.resizeHandle} ${styles.ne}`}
                  onMouseDown={(e) => handleResizeMouseDown(e, freeText.id, 'ne')}
                />
                <div
                  className={`${styles.resizeHandle} ${styles.sw}`}
                  onMouseDown={(e) => handleResizeMouseDown(e, freeText.id, 'sw')}
                />
                <div
                  className={`${styles.resizeHandle} ${styles.se}`}
                  onMouseDown={(e) => handleResizeMouseDown(e, freeText.id, 'se')}
                />
              </>
            )}
          </div>
        );
      })}
      </div>

      {/* Context menu */}
      {contextMenuId && contextMenuPos && (
        <FieldContextMenu
          x={contextMenuPos.x}
          y={contextMenuPos.y}
          fieldType="textfield"
          onEdit={handleEdit}
          onDelete={handleDelete}
          onClose={() => {
            setContextMenuId(null);
            setContextMenuPos(null);
          }}
        />
      )}

      {/* Edit modal */}
      {editModalId && (() => {
        const freeText = pageFreeTexts.find(ft => ft.id === editModalId);
        if (!freeText) return null;

        const fieldData: FieldData = {
          id: freeText.id,
          type: 'freetext',
          width: freeText.width,
          height: freeText.height,
          fontSize: freeText.fontSize,
          fontColor: freeText.fontColor,
          borderColor: freeText.borderColor,
          backgroundColor: freeText.backgroundColor,
          horizontalAlign: freeText.horizontalAlign,
        };

        return (
          <EditFieldModal
            field={fieldData}
            onSave={handleSaveEditModal}
            onClose={() => setEditModalId(null)}
          />
        );
      })()}
    </>
  );
};
